import { useEffect, useLayoutEffect, useRef } from 'react'
import mapboxgl, { GeoJSONSource } from 'mapbox-gl'

import { Coordinates } from '@/domain/protocols/Coordinates'

import { stackIds } from '../../containers/MapStackPresenter'
import { ObstacleFeatureInfoData } from '../../states/FeatureInfoState'
import { useMapContext } from '../MapView/MapContext'
import { isMapControlClick } from '../MapView/mapControlClick'
import { projectObstacleFeature } from './obstacleFormatters'
import {
  addObstacleSourceAndLayers,
  OBSTACLE_LAYER_IDS,
  OBSTACLE_SOURCE_ID,
  registerObstacleImages,
  removeObstacleLayersAndSource,
  setObstacleSelectionFilter
} from './obstacleLayerSpecs'

interface MapObstaclesLayerProps {
  dataUrl: string
  selectedObstacleId?: string
  onImageLoadError?: () => void
  onObstacleClick?: (obstacle: ObstacleFeatureInfoData) => void
  onLabelClick?: (obstacle: ObstacleFeatureInfoData) => void
  onClusterClick?: (coordinates: Coordinates, expansionZoom: number) => void
}

function MapObstaclesLayer(props: MapObstaclesLayerProps): null {
  const { dataUrl, selectedObstacleId, onImageLoadError, onObstacleClick, onLabelClick, onClusterClick } = props
  const { map } = useMapContext()
  const selectedObstacleIdRef = useRef(selectedObstacleId)
  const obstacleClickRef = useRef(onObstacleClick)
  const labelClickRef = useRef(onLabelClick)
  const clusterClickRef = useRef(onClusterClick)

  useLayoutEffect(() => {
    if (!map || !dataUrl) return

    let mounted = true

    void registerObstacleImages(map)
      .then((): void => {
        if (!mounted) return
        addObstacleSourceAndLayers(map, dataUrl, stackIds.obstacles, selectedObstacleIdRef.current)
      })
      .catch((): void => {
        if (mounted) onImageLoadError?.()
      })

    return () => {
      mounted = false
      removeObstacleLayersAndSource(map)
    }
  }, [map, dataUrl, onImageLoadError])

  useLayoutEffect(() => {
    if (!map) return

    selectedObstacleIdRef.current = selectedObstacleId
    setObstacleSelectionFilter(map, selectedObstacleId)
  }, [map, selectedObstacleId])

  useEffect(() => {
    obstacleClickRef.current = onObstacleClick
  }, [onObstacleClick])

  useEffect(() => {
    labelClickRef.current = onLabelClick
  }, [onLabelClick])

  useEffect(() => {
    clusterClickRef.current = onClusterClick
  }, [onClusterClick])

  useEffect(() => {
    if (!map || !dataUrl) return

    const handleObstacleClick = (event: mapboxgl.MapLayerMouseEvent) => {
      if (isMapControlClick(map, event.point)) return
      const feature = event.features?.[0]
      if (!feature) return

      const obstacle = projectObstacleFeature(feature)
      if (!obstacle) return

      obstacleClickRef.current?.(obstacle)
    }

    const handleLabelClick = (event: mapboxgl.MapLayerMouseEvent) => {
      if (isMapControlClick(map, event.point)) return
      const feature = event.features?.[0]
      if (!feature) return

      const obstacle = projectObstacleFeature(feature)
      if (!obstacle) return

      labelClickRef.current?.(obstacle)
    }

    const handleClusterClick = (event: mapboxgl.MapLayerMouseEvent) => {
      if (isMapControlClick(map, event.point)) return
      const feature = event.features?.[0]
      if (!feature) return

      const clusterId = feature.properties?.cluster_id
      if (typeof clusterId !== 'number') return

      const source = map.getSource(OBSTACLE_SOURCE_ID) as GeoJSONSource | undefined
      if (!source?.getClusterExpansionZoom) return

      const geometry = feature.geometry
      if (!geometry || geometry.type !== 'Point') return

      const [longitude, latitude] = geometry.coordinates ?? []
      if (typeof longitude !== 'number' || typeof latitude !== 'number') return

      source.getClusterExpansionZoom(clusterId, (error, zoom) => {
        if (error || typeof zoom !== 'number') return
        clusterClickRef.current?.(Coordinates.create({ latitude, longitude }), zoom)
      })
    }

    const handleMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer'
    }

    const handleMouseLeave = () => {
      map.getCanvas().style.cursor = ''
    }

    map.on('click', OBSTACLE_LAYER_IDS.icons, handleObstacleClick)
    map.on('click', OBSTACLE_LAYER_IDS.selectedIcons, handleObstacleClick)
    map.on('click', OBSTACLE_LAYER_IDS.clusters, handleClusterClick)
    map.on('click', OBSTACLE_LAYER_IDS.labels, handleLabelClick)
    map.on(
      'mouseenter',
      [
        OBSTACLE_LAYER_IDS.icons,
        OBSTACLE_LAYER_IDS.selectedIcons,
        OBSTACLE_LAYER_IDS.labels,
        OBSTACLE_LAYER_IDS.clusters
      ],
      handleMouseEnter
    )
    map.on(
      'mouseleave',
      [
        OBSTACLE_LAYER_IDS.icons,
        OBSTACLE_LAYER_IDS.selectedIcons,
        OBSTACLE_LAYER_IDS.labels,
        OBSTACLE_LAYER_IDS.clusters
      ],
      handleMouseLeave
    )

    return () => {
      map.off('click', OBSTACLE_LAYER_IDS.icons, handleObstacleClick)
      map.off('click', OBSTACLE_LAYER_IDS.selectedIcons, handleObstacleClick)
      map.off('click', OBSTACLE_LAYER_IDS.clusters, handleClusterClick)
      map.off('click', OBSTACLE_LAYER_IDS.labels, handleLabelClick)
      map.off(
        'mouseenter',
        [
          OBSTACLE_LAYER_IDS.icons,
          OBSTACLE_LAYER_IDS.selectedIcons,
          OBSTACLE_LAYER_IDS.labels,
          OBSTACLE_LAYER_IDS.clusters
        ],
        handleMouseEnter
      )
      map.off(
        'mouseleave',
        [
          OBSTACLE_LAYER_IDS.icons,
          OBSTACLE_LAYER_IDS.selectedIcons,
          OBSTACLE_LAYER_IDS.labels,
          OBSTACLE_LAYER_IDS.clusters
        ],
        handleMouseLeave
      )
    }
  }, [map, dataUrl])

  return null
}

export { MapObstaclesLayer }
